// ════════════════════════════════════════════════════════════════════
// WorkflowEditor — full-screen n8n-style canvas editor for a single
// workflow definition. Replaces the legacy form-based WorkflowBuilderDialog.
//
// Flow:
//   - Loads {steps, edges} via apiGetWorkflowDefinition.
//   - Renders nodes positioned from steps.position_x/y, or auto-laid-out
//     via dagre when positions are missing (legacy workflows backfilled
//     by Phase 1 have edges but no positions).
//   - Drag from palette → drop on canvas: POST a new step with position.
//   - Drag between handles: POST a new edge.
//   - Click a node: opens NodeConfigPanel (right sidebar).
//   - Node drag end: PATCH position to /steps/:id/position.
//   - Edge delete: DELETE /edges/:id.
//   - Node delete: DELETE /definitions/:defId/steps/:stepId.
// ════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, Panel,
  ReactFlowProvider, useNodesState, useEdgesState, useReactFlow,
  type Node, type Edge, type Connection, type OnConnect, type NodeChange,
  type EdgeChange, addEdge, applyEdgeChanges, applyNodeChanges,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Zap, Sparkles, AlertCircle, LayoutGrid } from "lucide-react";
import {
  apiGetWorkflowDefinition, apiGetWorkflowTriggers, apiUpdateWorkflowDefinition,
  apiAddWorkflowStep, apiUpdateWorkflowStep, apiDeleteWorkflowStep,
  apiAddWorkflowEdge, apiDeleteWorkflowEdge, apiUpdateStepPosition,
  type WorkflowDefinitionDetail, type WorkflowStep, type WorkflowStepType,
  type WorkflowTrigger,
} from "@/lib/api";
import {
  NODE_TYPES, NODE_WIDTH, NODE_HEIGHT, flowNodeTypeFor, STEP_META,
  type StepNodeData, type FlowNode,
} from "./nodes";
import { NodePalette, PALETTE_DRAG_MIME } from "./NodePalette";
import { NodeConfigPanel } from "./NodeConfigPanel";

// ── Default config seed per step type ───────────────────────────────
function defaultConfigFor(type: WorkflowStepType): Record<string, any> {
  switch (type) {
    case "condition": return { field: "", operator: "eq", value: "" };
    case "parallel":  return {};
    case "merge":     return {};
    case "delay":     return { delay_days: 1, delay_type: "after_trigger" };
    case "switch":    return { field: "", cases: [] };
    case "service_action": return { action: "" };
    case "notification":   return { recipientRole: "employee", notifTitle: "", notifMessage: "" };
    default: return {};
  }
}

function defaultOutputsCount(type: WorkflowStepType): number {
  if (type === "condition") return 2;
  if (type === "parallel" || type === "switch") return 2;
  if (type === "end") return 0;
  return 1;
}

// ── Dagre layout (fallback for nodes without saved positions) ──────
function autoLayout(nodes: FlowNode[], edges: Edge[]): FlowNode[] {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 80, ranksep: 100 });
  nodes.forEach((n) => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const pos = g.node(n.id);
    return pos ? { ...n, position: { x: pos.x - NODE_WIDTH / 2, y: pos.y - NODE_HEIGHT / 2 } } : n;
  });
}

// ── Build {nodes, edges} from an API definition ─────────────────────
function buildGraph(
  def: WorkflowDefinitionDetail,
  triggerLabel: string,
): { nodes: FlowNode[]; edges: Edge[] } {
  // Synthetic trigger node anchors the workflow.
  const triggerNode: FlowNode = {
    id: "trigger",
    type: "trigger",
    position: { x: 0, y: 0 },
    data: { step: {} as WorkflowStep, isTrigger: true, triggerLabel },
    deletable: false,
  };

  const stepNodes: FlowNode[] = def.steps.map((s) => ({
    id: `s${s.id}`,
    type: flowNodeTypeFor(s.step_type),
    position: {
      x: typeof s.position_x === "number" ? s.position_x : 0,
      y: typeof s.position_y === "number" ? s.position_y : 0,
    },
    data: { step: s },
  }));

  const allNodes = [triggerNode, ...stepNodes];

  // Edges from def.edges (workflow_edges table). Fall back to step_order
  // chain for workflows that haven't been backfilled.
  const edges: Edge[] = [];
  const stepsById = new Map(def.steps.map((s) => [s.id, s]));

  if (def.edges && def.edges.length > 0) {
    for (const e of def.edges) {
      const handleStyle =
        e.source_handle === "true"  ? { stroke: "#10b981" } :
        e.source_handle === "false" ? { stroke: "#f43f5e" } :
        e.source_handle.startsWith("case_")   ? { stroke: "#d946ef" } :
        e.source_handle.startsWith("branch_") ? { stroke: "#06b6d4" } :
        { stroke: "#94a3b8" };
      edges.push({
        id: `e${e.id}`,
        source: `s${e.source_step_id}`,
        sourceHandle: e.source_handle === "out" ? undefined : e.source_handle,
        target: `s${e.target_step_id}`,
        label: e.source_handle !== "out" ? e.source_handle : undefined,
        labelStyle: { fontSize: 10, fontWeight: 600 },
        style: { ...handleStyle, strokeWidth: 1.5 },
        data: { edgeId: e.id },
      });
    }
  } else {
    // Legacy fallback: derive linear chain from step_order.
    const sorted = [...def.steps].sort((a, b) => a.step_order - b.step_order);
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i].step_type === "condition") continue;
      edges.push({
        id: `eorder-${sorted[i].id}-${sorted[i + 1].id}`,
        source: `s${sorted[i].id}`,
        target: `s${sorted[i + 1].id}`,
        style: { stroke: "#94a3b8", strokeWidth: 1.5, strokeDasharray: "4 4" },
      });
    }
  }

  // Always add a trigger → first step edge (synthetic; not stored).
  const firstStep = def.steps.sort((a, b) => a.step_order - b.step_order)[0];
  if (firstStep) {
    edges.unshift({
      id: "etrigger",
      source: "trigger",
      target: `s${firstStep.id}`,
      style: { stroke: "#a78bfa", strokeWidth: 1.5 },
      deletable: false,
      selectable: false,
    });
  }

  // Apply dagre layout to any node missing stored x/y.
  const needsLayout = stepNodes.some((n) => n.position.x === 0 && n.position.y === 0);
  const finalNodes = needsLayout ? autoLayout(allNodes, edges) : allNodes;
  // Trigger always at the top.
  return {
    nodes: finalNodes.map((n) => n.id === "trigger" ? { ...n, position: { x: n.position.x, y: -160 } } : n),
    edges,
  };
}

// ════════════════════════════════════════════════════════════════════
// EditorInner — wrapped in ReactFlowProvider for useReactFlow access.
// ════════════════════════════════════════════════════════════════════
function EditorInner({
  def, triggers, onClose, refetch,
}: {
  def: WorkflowDefinitionDetail;
  triggers: WorkflowTrigger[];
  onClose: () => void;
  refetch: () => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { screenToFlowPosition } = useReactFlow();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const triggerLabel = useMemo(
    () => triggers.find((tr) => tr.event === def.trigger_event)?.label || def.trigger_event,
    [triggers, def.trigger_event],
  );

  const initial = useMemo(() => buildGraph(def, triggerLabel), [def, triggerLabel]);

  const [nodes, setNodes] = useNodesState<FlowNode>(initial.nodes);
  const [edges, setEdges] = useEdgesState<Edge>(initial.edges);
  const [selectedStepId, setSelectedStepId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [defActive, setDefActive] = useState(def.is_active);
  const [defName, setDefName] = useState(def.name);

  // Re-sync when the upstream definition changes (after a save/refetch).
  useEffect(() => {
    setNodes(initial.nodes);
    setEdges(initial.edges);
  }, [initial.nodes, initial.edges, setNodes, setEdges]);

  // ── Selected step (drives the right sidebar) ──────────────────────
  const selectedStep = selectedStepId
    ? def.steps.find((s) => s.id === selectedStepId) || null
    : null;

  // ── Node changes (drag, select, remove) ───────────────────────────
  const handleNodesChange = useCallback((changes: NodeChange<FlowNode>[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
    // Persist position on drag stop.
    for (const ch of changes) {
      if (ch.type === "position" && !ch.dragging && ch.position && ch.id.startsWith("s")) {
        const stepId = Number(ch.id.slice(1));
        if (Number.isFinite(stepId)) {
          apiUpdateStepPosition(stepId, Math.round(ch.position.x), Math.round(ch.position.y))
            .catch((err) => console.warn("[editor] position save failed:", err.message));
        }
      }
    }
  }, [setNodes]);

  const handleEdgesChange = useCallback((changes: EdgeChange<Edge>[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
    for (const ch of changes) {
      if (ch.type === "remove") {
        const edge = edges.find((e) => e.id === ch.id);
        const edgeId = (edge?.data as any)?.edgeId;
        if (typeof edgeId === "number") {
          apiDeleteWorkflowEdge(edgeId).catch((err) =>
            toast({ title: "Edge delete failed", description: err.message, variant: "destructive" }));
        }
      }
    }
  }, [edges, setEdges, toast]);

  // ── Connect (new edge via handle drag) ────────────────────────────
  const onConnect: OnConnect = useCallback(async (conn: Connection) => {
    if (!conn.source || !conn.target) return;
    if (!conn.source.startsWith("s") || !conn.target.startsWith("s")) {
      toast({ title: "Cannot connect from trigger output via canvas — auto-wired to first step.", variant: "destructive" });
      return;
    }
    const sourceId = Number(conn.source.slice(1));
    const targetId = Number(conn.target.slice(1));
    if (!Number.isFinite(sourceId) || !Number.isFinite(targetId)) return;
    if (sourceId === targetId) return;
    try {
      const handle = conn.sourceHandle || "out";
      const apiEdge = await apiAddWorkflowEdge(def.id, {
        source_step_id: sourceId, source_handle: handle, target_step_id: targetId,
      });
      setEdges((eds) => addEdge({
        ...conn,
        id: `e${apiEdge.id}`,
        sourceHandle: handle === "out" ? undefined : handle,
        label: handle !== "out" ? handle : undefined,
        style: {
          stroke: handle === "true" ? "#10b981" : handle === "false" ? "#f43f5e" :
                  handle.startsWith("case_") ? "#d946ef" :
                  handle.startsWith("branch_") ? "#06b6d4" : "#94a3b8",
          strokeWidth: 1.5,
        },
        data: { edgeId: apiEdge.id },
      } as Connection, eds));
    } catch (err: any) {
      toast({ title: "Edge create failed", description: err.message, variant: "destructive" });
    }
  }, [def.id, setEdges, toast]);

  // ── Drop from palette ─────────────────────────────────────────────
  const onDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const type = e.dataTransfer.getData(PALETTE_DRAG_MIME) as WorkflowStepType;
    if (!type) return;
    const meta = STEP_META[type];
    if (!meta) return;

    const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    try {
      const created = await apiAddWorkflowStep(def.id, {
        name: `New ${meta.label}`,
        step_type: type,
        config: defaultConfigFor(type),
        position_x: Math.round(pos.x),
        position_y: Math.round(pos.y),
        outputs_count: defaultOutputsCount(type),
      });
      refetch();
      // Select the new node so the config panel opens right away.
      setTimeout(() => setSelectedStepId(created.id), 50);
    } catch (err: any) {
      toast({ title: "Add step failed", description: err.message, variant: "destructive" });
    }
  }, [def.id, refetch, screenToFlowPosition, toast]);

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(PALETTE_DRAG_MIME)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  // ── Selection ─────────────────────────────────────────────────────
  const onNodeClick = useCallback((_e: React.MouseEvent, node: Node) => {
    if (node.id === "trigger") { setSelectedStepId(null); return; }
    if (node.id.startsWith("s")) setSelectedStepId(Number(node.id.slice(1)));
  }, []);

  const onPaneClick = useCallback(() => setSelectedStepId(null), []);

  // ── Save / delete node from the config panel ──────────────────────
  const handleSaveStep = async (patch: Partial<WorkflowStep>) => {
    if (!selectedStep) return;
    setSaving(true);
    try {
      await apiUpdateWorkflowStep(def.id, selectedStep.id, patch);
      toast({ title: "Step saved" });
      refetch();
    } catch (err: any) {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async () => {
    if (!selectedStep) return;
    if (!confirm(`Delete step "${selectedStep.name}"? Connected edges will be removed.`)) return;
    try {
      await apiDeleteWorkflowStep(def.id, selectedStep.id);
      setSelectedStepId(null);
      refetch();
    } catch (err: any) {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    }
  };

  // ── Toolbar handlers ──────────────────────────────────────────────
  const handleToggleActive = async () => {
    try {
      await apiUpdateWorkflowDefinition(def.id, { is_active: !defActive });
      setDefActive(!defActive);
      qc.invalidateQueries({ queryKey: ["workflow-definitions"] });
      toast({ title: defActive ? "Deactivated" : "Activated" });
    } catch (err: any) {
      toast({ title: "Toggle failed", description: err.message, variant: "destructive" });
    }
  };

  const handleRename = async () => {
    if (defName.trim() === def.name) return;
    try {
      await apiUpdateWorkflowDefinition(def.id, { name: defName.trim() });
      qc.invalidateQueries({ queryKey: ["workflow-definitions"] });
      toast({ title: "Workflow renamed" });
    } catch (err: any) {
      toast({ title: "Rename failed", description: err.message, variant: "destructive" });
    }
  };

  // ── Validation — flag nodes with obvious config issues ───────────
  // Currently checks: orphan (no inbound + not the first/root step),
  // service_action without an action, condition without a field, and
  // approval/task without an assignee. The validator is intentionally
  // conservative to avoid noisy red badges on workflows the user is
  // still building.
  const { invalidStepIds, validationIssues } = useMemo(() => {
    const issues: { stepId: number; message: string }[] = [];
    const invalidIds = new Set<number>();
    const targetIds = new Set<number>();
    for (const e of edges) {
      if (e.target.startsWith("s")) targetIds.add(Number(e.target.slice(1)));
    }
    const firstStepId = [...def.steps].sort((a, b) => a.step_order - b.step_order)[0]?.id;

    for (const step of def.steps) {
      if (step.id !== firstStepId && !targetIds.has(step.id)) {
        invalidIds.add(step.id);
        issues.push({ stepId: step.id, message: `"${step.name}" is unreachable — connect an incoming edge.` });
      }
      if (step.step_type === "service_action" && !step.config?.action) {
        invalidIds.add(step.id);
        issues.push({ stepId: step.id, message: `"${step.name}" is missing an action.` });
      }
      if (step.step_type === "condition" && !step.config?.field) {
        invalidIds.add(step.id);
        issues.push({ stepId: step.id, message: `"${step.name}" condition has no field set.` });
      }
      if ((step.step_type === "approval" || step.step_type === "task") && !step.assign_to_role && !step.assign_to_user_id) {
        invalidIds.add(step.id);
        issues.push({ stepId: step.id, message: `"${step.name}" has no assignee.` });
      }
    }
    return { invalidStepIds: invalidIds, validationIssues: issues };
  }, [def.steps, edges]);

  // Inject `selected` and `invalid` into node data so renderers can highlight.
  const decoratedNodes = useMemo(
    () => nodes.map((n) => {
      const stepId = n.id.startsWith("s") ? Number(n.id.slice(1)) : null;
      return {
        ...n,
        data: {
          ...n.data,
          selected: n.id === `s${selectedStepId}`,
          invalid: stepId != null && invalidStepIds.has(stepId),
        },
      };
    }),
    [nodes, selectedStepId, invalidStepIds],
  );

  // ── Auto-layout — re-runs dagre over the current graph and persists
  //    every new position to the backend.
  const handleAutoLayout = useCallback(async () => {
    const laidOut = autoLayout(nodes, edges);
    setNodes(laidOut);
    // Persist (best-effort; ignore individual failures).
    await Promise.allSettled(
      laidOut
        .filter((n) => n.id.startsWith("s"))
        .map((n) => apiUpdateStepPosition(Number(n.id.slice(1)), Math.round(n.position.x), Math.round(n.position.y))),
    );
    toast({ title: "Auto-laid-out" });
  }, [nodes, edges, setNodes, toast]);

  // ── Cmd/Ctrl+S — manual save trigger (positions/edges autosave, so this
  //    just refreshes the data and shows confirmation).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        refetch();
        toast({ title: "Saved", description: "Positions and edges autosave; canvas refreshed." });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [refetch, toast]);

  return (
    <div className="flex h-full w-full">
      <NodePalette />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* ── Toolbar ────────────────────────────────────── */}
        <div className="border-b border-border bg-card/60 px-4 py-2 flex items-center gap-3">
          <Zap className="w-4 h-4 text-violet-600" />
          <Input
            className="h-8 max-w-xs text-sm font-medium"
            value={defName}
            onChange={(e) => setDefName(e.target.value)}
            onBlur={handleRename}
          />
          <Badge variant="outline" className="text-xs">
            <Sparkles className="w-3 h-3 mr-1" />
            {triggerLabel}
          </Badge>
          <div className="flex items-center gap-2 ml-2">
            <Switch checked={defActive} onCheckedChange={handleToggleActive} />
            <span className="text-xs text-muted-foreground">
              {defActive ? "Active" : "Inactive"}
            </span>
          </div>
          {validationIssues.length > 0 && (
            <button
              type="button"
              className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-1"
              onClick={() => {
                const first = validationIssues[0];
                setSelectedStepId(first.stepId);
                toast({
                  title: `${validationIssues.length} issue${validationIssues.length === 1 ? "" : "s"}`,
                  description: validationIssues.map((i) => "• " + i.message).join("\n"),
                });
              }}
              title="Click to inspect"
            >
              <AlertCircle className="w-3.5 h-3.5" />
              {validationIssues.length} issue{validationIssues.length === 1 ? "" : "s"}
            </button>
          )}
          <span className="text-xs text-muted-foreground ml-auto">
            {def.steps.length} step{def.steps.length === 1 ? "" : "s"} · {def.edges?.length ?? 0} edge{(def.edges?.length ?? 0) === 1 ? "" : "s"}
          </span>
          <Button variant="outline" size="sm" onClick={handleAutoLayout} title="Re-layout with dagre">
            <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Auto-layout
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>

        {/* ── Canvas ─────────────────────────────────────── */}
        <div
          ref={wrapperRef}
          className="flex-1 min-h-0 relative bg-muted/20"
          onDrop={onDrop}
          onDragOver={onDragOver}
        >
          <ReactFlow
            nodes={decoratedNodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            nodeTypes={NODE_TYPES}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.2}
            maxZoom={2}
            deleteKeyCode={["Backspace", "Delete"]}
            connectionRadius={28}
          >
            <Background gap={20} size={1} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable nodeStrokeWidth={2} />
            {def.steps.length === 0 && (
              <Panel position="top-center" className="bg-card border border-border rounded-lg px-4 py-2 shadow-sm text-sm text-muted-foreground">
                Drag a node from the left palette to start building.
              </Panel>
            )}
          </ReactFlow>
        </div>
      </div>

      {/* ── Right config panel (mounted only when a step is selected) ── */}
      {selectedStep && (
        <NodeConfigPanel
          step={selectedStep}
          saving={saving}
          onSave={handleSaveStep}
          onDelete={handleDeleteStep}
          onClose={() => setSelectedStepId(null)}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// WorkflowEditorDialog — public entry. Wraps EditorInner in
// ReactFlowProvider and a full-screen Dialog.
// ════════════════════════════════════════════════════════════════════
export function WorkflowEditorDialog({
  defId, open, onClose,
}: { defId: number | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const enabled = open && defId != null;

  const { data: def, isLoading, error, refetch } = useQuery({
    queryKey: ["workflow-definition", defId],
    queryFn: () => apiGetWorkflowDefinition(defId!),
    enabled,
  });

  const { data: triggers = [] } = useQuery({
    queryKey: ["workflow-triggers"],
    queryFn: apiGetWorkflowTriggers,
    enabled,
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[95vh] p-0 gap-0 flex flex-col [&>button:last-child]:hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>{def?.name || t("workflows.builder")}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0">
          {isLoading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              {t("workflows.loading")}
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-rose-600 text-sm gap-2">
              <AlertCircle className="w-4 h-4" />
              {(error as Error).message}
            </div>
          ) : def ? (
            <ReactFlowProvider>
              <EditorInner def={def} triggers={triggers} onClose={onClose} refetch={refetch} />
            </ReactFlowProvider>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
